import React, { memo, useCallback, useState } from 'react';
import { Map } from 'immutable';
import { connect } from 'react-redux';
import { isEmpty } from 'underscore';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { unwrap } from '../../utils/ChangeSpy';

import {
  changeContactField,
  saveContactField,
  revertContactField,
  closeValidationError,
} from '../../actions/contactDetail';
import checkZip from '../services/zipRules';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { usePopupContext } from '../../contexts/PopupContext';

function validate(values, { zip: zipBlurred = false }) {
  const { zip, showCountry } = values;
  const errors = {};

  if (zipBlurred && !showCountry && !checkZip(zip)) {
    errors.zip =
      'Zip code should follow U.S. (XXXXX or XXXXX-XXXX) or Canadian (A1A 1A1) format; otherwise, please set address to International ("...")';
  }

  return errors;
}

const EditAddressPopupContainer = ({
  children,
  states,
  contactAddress,
  addressIndex,
  changeContactField,
  saveContactField,
}) => {
  const context = usePopupContext();
  const [errors, setErrors] = useState({});

  const validateForm = useCallback(
    (blurred = {}) => {
      const zip = unwrap(contactAddress.get('zip'));
      const showCountry = unwrap(contactAddress.get('showCountry'));
      const formErrors = validate({ zip, showCountry }, blurred);

      setErrors(formErrors);

      return formErrors;
    },
    [contactAddress],
  );

  const handleErrorClose = useCallback((event, field) => {
    setErrors({ [field]: '' });
  }, []);

  const handleChange = useCallback(
    event => {
      const {
        target: { name, value },
      } = event;
      const field = 'addresses';

      changeContactField({ field, name, value, index: addressIndex });
    },
    [changeContactField, addressIndex],
  );

  const handleClose = useCallback(() => {
    context.closePopup();
  }, [context]);

  const handleSave = useCallback(() => {
    const formErrors = validateForm({ zip: true });

    if (isEmpty(formErrors)) {
      saveContactField();
      handleClose();
    }
  }, [validateForm, saveContactField, handleClose]);

  const handleBlur = useCallback(
    ({ target: { name } }) => {
      validateForm({ [name]: true });
    },
    [validateForm],
  );

  const handleTriggerShowCountry = useCallback(() => {
    const name = 'showCountry';
    const value = !contactAddress.getIn(['showCountry', 'value'], false);
    const field = 'addresses';

    changeContactField({ field, name, value, index: addressIndex });
  }, [changeContactField, contactAddress, addressIndex]);

  return (
    <PrettyErrorProvider inputErrors={Map(errors)} onErrorClose={handleErrorClose}>
      <div>
        <Popups.EditAddressPopup
          address={contactAddress}
          onBlur={handleBlur}
          onChange={handleChange}
          onClose={handleClose}
          onSave={handleSave}
          onTriggerShowCountry={handleTriggerShowCountry}
          states={states}
        />
        {children}
      </div>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    addressIndex: props.popup.getIn(['props', 'index']),
    states: state.contact.info.get('states'),
    contactAddress: state.contact.info.getIn(state.contact.info.get('editing')),
  };
}

export { EditAddressPopupContainer };
export default connect(
  mapStateToProps,
  {
    changeContactField,
    saveContactField,
    revertContactField,
    closeValidationError,
  },
  mergeProps,
  connectOptions,
)(memo(EditAddressPopupContainer));
